// Small pieces shared by the Connections screens. No words here: every label is
// the caller's or a phrase key, so the English guard has nothing to read.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Button } from '@heroui/react'
import { Dialog as UiDialog } from '../ui/Dialog'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'

const MARKS = {
  lock: 'M6 11h12v10H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  send: 'M4 12l16-8-6 16-3-7zM11 13l9-9',
  key: 'M14.5 13a4.5 4.5 0 1 0-4-2.5L4 17v3h3v-2h2v-2h2l1.5-1.5a4.5 4.5 0 0 0 2 .5zM16 7.5v.01',
  feed: 'M4 6c0-1.1 3.6-2 8-2s8 .9 8 2-3.6 2-8 2-8-.9-8-2zM4 6v12c0 1.1 3.6 2 8 2s8-.9 8-2V6M4 12c0 1.1 3.6 2 8 2s8-.9 8-2',
} as const

export function Mark({ name, size = 16 }: { name: keyof typeof MARKS; size?: number }) {
  return (
    <svg
      className="connect-mark"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={MARKS[name]} />
    </svg>
  )
}

/** "CN=Tellers,OU=Groups,..." reads as "Tellers"; anything else as typed. */
export function shortGroup(dn: string): string {
  return /^\s*cn=([^,]+)/i.exec(dn)?.[1]?.trim() ?? dn
}

/** A stored secret, which is never sent back: only whether one is set. */
export function Masked({ children }: { children?: ReactNode }) {
  return (
    <span className="connect-masked">
      <Mark name="key" size={14} />
      <span className="connect-masked__dots" aria-hidden="true">
        ••••••••
      </span>
      {children}
    </span>
  )
}

/** A test's answer: a tick or a warning, one bold line, then the detail. */
export function TestResult({ ok, title, children }: { ok: boolean; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="connect-result" data-ok={ok || undefined} role="status">
      <span className="connect-result__icon" aria-hidden="true">
        <Icon name={ok ? 'check' : 'alert'} size={16} />
      </span>
      <span className="connect-result__text">
        <strong>{title}</strong>
        {children}
      </span>
    </div>
  )
}

/**
 * A secret the server sends once: masked until Show, with Copy, until Done.
 * The clipboard is refused on plain http, so Show is how it is then read.
 */
export function OneTimeSecret({
  title,
  note,
  value,
  labels,
  onCopied,
  onDone,
  role = 'alert',
}: {
  title: ReactNode
  note: ReactNode
  value: string
  labels: { show: string; hide: string; copy: string; done: string }
  onCopied?: () => void
  onDone: () => void
  role?: 'alert' | 'alertdialog'
}) {
  const [shown, setShown] = useState(false)
  const canCopy = typeof navigator !== 'undefined' && Boolean(navigator.clipboard)
  return (
    <div className="connect-secret" role={role} aria-label={typeof title === 'string' ? title : undefined}>
      <div className="connect-secret__text">
        <strong>{title}</strong>
        <span>{note}</span>
      </div>
      <div className="connect-secret__value">
        <code data-shown={shown || undefined}>{shown ? value : '•'.repeat(Math.min(value.length, 32))}</code>
        <Button variant="tertiary" size="sm" aria-pressed={shown} onPress={() => setShown((s) => !s)}>
          {shown ? labels.hide : labels.show}
        </Button>
      </div>
      <div className="connect-secret__actions">
        {canCopy && (
          <Button variant="secondary" onPress={() => void navigator.clipboard.writeText(value).then(() => onCopied?.(), () => {})}>
            <Icon name="copy" size={16} /> {labels.copy}
          </Button>
        )}
        <Button variant="ghost" onPress={onDone}>
          {labels.done}
        </Button>
      </div>
    </div>
  )
}

/** Brings a refusal shown at the top of a scrolled dialog into view after a save. */
export function revealNotice(root: Pick<ParentNode, 'querySelector'> | null): void {
  root?.querySelector('[role=alert], .notice')?.scrollIntoView({ block: 'nearest' })
}

/**
 * A form in the shared dark-header dialog: Enter or the primary button submits.
 * Escape, the scrim or the cross ask first once something has been typed.
 */
export function Dialog({
  title,
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  extra,
  wide = false,
  children,
}: {
  title: string
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  busy?: boolean
  /** Left of the footer: a destructive action or a short note. */
  extra?: ReactNode
  wide?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const formId = useId()
  const dirty = useRef(false)
  const form = useRef<HTMLFormElement>(null)
  const wasBusy = useRef(busy)
  const [asking, setAsking] = useState(false)
  const submit = Boolean(onSubmit && submitLabel)
  // A save that went through: what was typed is now what is stored.
  useEffect(() => {
    if (!busy) dirty.current = false
    if (wasBusy.current && !busy) revealNotice(form.current)
    wasBusy.current = busy
  }, [busy])
  const leave = () => (dirty.current ? setAsking(true) : onClose())

  const footer = asking ? (
    <span className="connect-ask-close" role="alertdialog" aria-label={say('ui.discardAsk')}>
      <strong>{say('ui.discardAsk')}</strong>
      <Button variant="ghost" size="sm" autoFocus onPress={() => setAsking(false)}>
        {say('ui.keepEditing')}
      </Button>
      <Button variant="danger" size="sm" onPress={onClose}>
        {say('ui.discard')}
      </Button>
    </span>
  ) : (
    <>
      <Button variant="ghost" onPress={onClose}>
        {submit ? say('common.cancel') : say('ui.close')}
      </Button>
      {submit && (
        <Button type="submit" form={formId} variant="primary" isDisabled={!valid || busy}>
          {busy ? say('common.saving') : submitLabel}
        </Button>
      )}
    </>
  )

  return (
    <UiDialog title={title} tone="dark" wide={wide} onClose={leave} footer={footer} footNote={asking ? undefined : extra}>
      <form
        ref={form}
        id={formId}
        className="connect-dialog"
        // A submit stops here: a dialog opened from inside another form must not save that one.
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (valid && !busy && onSubmit) onSubmit()
        }}
        onInput={() => {
          dirty.current = true
        }}
        onChange={() => {
          dirty.current = true
        }}
      >
        {children}
      </form>
    </UiDialog>
  )
}
